import { format, isAfter, isSameDay } from 'date-fns';
import {
  Appointments,
  IdentificationType,
  WaiverRequests,
  WaiverStatus,
} from '@prisma/client';
import {
  GetWaiversData,
  WaiverRequestWithCustomerAndEvents,
  WaiverResponseData,
} from '../../types/waivers';
import { Appointment, LoadedUser } from '../../interfaces';
import { getBusinessShopUrl } from '../businesses';
import {
  convertSnakeToTitleCase,
  getCleanedPhone,
  isMinor,
  openExternalUrl,
} from '../../utils/global/helper';
import { CustomerDetails } from '../../types/appointments';

export function formatWaiverEventDate(date: string | Date | null) {
  if (!date) return 'N/A';
  return format(new Date(date), 'MM/dd/yyyy');
}

export function getWaiverLastUpdateText(
  waiver: WaiverRequestWithCustomerAndEvents,
) {
  switch (waiver.status) {
    case 'CANCELED': {
      const date =
        waiver.events.find(event => event.type === 'CANCELED')?.createdAt ??
        waiver.updatedAt;
      return `Canceled on ${formatWaiverEventDate(date)}`;
    }

    case 'QUEUED': {
      const date =
        waiver.events.find(event => event.type === 'SCHEDULED')?.createdAt ??
        waiver.createdAt;
      return `Queued on ${formatWaiverEventDate(date)}`;
    }

    case 'SENT': {
      const date =
        waiver.events.find(
          event => event.type === 'SENT' || event.type === 'REMINDER',
        )?.createdAt ?? waiver.updatedAt;
      return `Sent on ${formatWaiverEventDate(date)}`;
    }

    case 'SIGNED': {
      const date =
        waiver.events.find(event => event.type === 'COMPLETED')?.createdAt ??
        waiver.updatedAt;
      return `Signed on ${formatWaiverEventDate(date)}`;
    }

    default:
      return 'N/A';
  }
}

export const isEmployeeWaiverEnabled = (
  waiver: GetWaiversData | undefined | null,
) =>
  !!waiver?.employeeSettings?.enabled &&
  waiver.employeeSettings.options.length > 0;

export const getWaiverRequestStatus = ({
  appointment,
  waiverRequest,
  waivers,
}: {
  appointment:
    | Pick<
        Appointments | Appointment,
        'checkoutSessionCompleted' | 'status' | 'startDateTime'
      >
    | null
    | undefined;
  waiverRequest: WaiverRequests | null | undefined;
  waivers: GetWaiversData[] | undefined | null;
}) => {
  if (!appointment) return null;
  if (!waiverRequest) {
    return 'noWaiverRequest';
  }

  const waiver = waivers?.find(w => w.id === waiverRequest.waiverId);

  const isCanceled = waiverRequest.status === 'CANCELED';
  const isCompletedByCustomer =
    waiverRequest.status === 'SIGNED' && waiverRequest.signedFileLink;
  const willNeverComplete =
    appointment.status === 'CANCELED' && !isCompletedByCustomer;

  const isEmployeeWaiverComplete =
    !!(waiverRequest.responseData as WaiverResponseData)?.employee &&
    !!waiverRequest.previousSignedFileLink;
  const isPendingEmployeeWaiver =
    isCompletedByCustomer &&
    isEmployeeWaiverEnabled(waiver) &&
    !isEmployeeWaiverComplete;

  const status = isPendingEmployeeWaiver
    ? 'pendingEmployee'
    : isCompletedByCustomer
    ? 'complete'
    : isCanceled
    ? 'canceled'
    : willNeverComplete
    ? 'willNeverComplete'
    : 'notCompleted';

  return status;
};

export function getWaiverNextUpdateText({
  appointment,
  waiverRequest,
  waivers,
}: Parameters<typeof getWaiverRequestStatus>[0]) {
  const status =
    waiverRequest?.status === 'QUEUED'
      ? 'queued'
      : getWaiverRequestStatus({ appointment, waiverRequest, waivers });

  switch (status) {
    case 'queued': {
      const date = appointment?.startDateTime ?? null;
      return `Sending on ${formatWaiverEventDate(date)}`;
    }

    case 'notCompleted': {
      return `Customer signature`;
    }

    case 'pendingEmployee': {
      return 'Employee signature';
    }

    default:
      return 'None';
  }
}

export function getWaiverManagementPermission(user: LoadedUser) {
  if (user.business.type === 'STUDIO' && user.role !== 'SHOP_MANAGER')
    return false;

  return user.canManageWaivers;
}

export function isAppointmentWaiverUpdatable(
  startDateTime: Date | string | undefined | null,
) {
  const now = new Date();
  const startDate = startDateTime ? new Date(startDateTime) : null;
  return !!startDate && (isSameDay(startDate, now) || isAfter(startDate, now));
}

export function previewWaiver(
  business: LoadedUser['business'],
  waiverId: string,
) {
  if (!business || !waiverId) return;
  openExternalUrl(
    getBusinessShopUrl(business, `/preview-waiver?waiver=${waiverId}`),
  );
}

export const getWaiverRequestStatusColor = ({
  appointment,
  waiverRequest,
  waivers,
}: Parameters<typeof getWaiverRequestStatus>[0]) => {
  const status = getWaiverRequestStatus({
    appointment,
    waiverRequest,
    waivers,
  });
  if (!status) return '#FF3008';

  return status === 'pendingEmployee'
    ? '#EC942C'
    : status === 'complete'
    ? '#00A86B'
    : '#FF3008';
};

export const getDefaultWaiver = ({
  waivers,
  status = 'PUBLISHED',
}: {
  waivers: GetWaiversData[] | undefined;
  status?: WaiverStatus;
}) => waivers?.find(waiver => waiver.isDefault && waiver.status === status);

export const getServiceWaiver = ({
  waivers,
  serviceId,
  status = 'PUBLISHED',
}: {
  waivers: GetWaiversData[] | undefined;
  serviceId: number;
  status?: WaiverStatus;
}) =>
  waivers?.find(
    waiver =>
      waiver.services.some(service => service.serviceId === serviceId) &&
      waiver.status === status,
  );

const capitalizeID = (text: string) => {
  const words = text.split(' ');
  return words.map(word => (word === 'Id' ? 'ID' : word)).join(' ');
};

export const getIDUploadTitle = (idType: IdentificationType, suffix?: string) =>
  `${capitalizeID(convertSnakeToTitleCase(idType || 'ID'))}${
    suffix ? ` ${suffix}` : ''
  }`;

export const getWaiverCustomerDetails = (
  waiverRequest: WaiverRequestWithCustomerAndEvents,
): CustomerDetails => {
  const { customer, appointment } = waiverRequest;

  const customerDob = customer.dob
    ? new Date(customer.dob).toISOString().split('T')[0]
    : undefined;
  const customerPhoneNumber = getCleanedPhone(customer.user.phoneNumber);

  if (appointment.customerGuest) {
    const guestDob = appointment.customerGuest.dob
      ? new Date(appointment.customerGuest.dob).toISOString().split('T')[0]
      : undefined;
    const guestIsMinor = isMinor(guestDob, appointment.startDateTime);
    const guestRelation = appointment.customerGuest.relation ?? 'OTHER';
    return {
      firstName: appointment.customerGuest.firstName || '',
      lastName: appointment.customerGuest.lastName || '',
      dob: guestDob,
      phoneNumber: undefined,
      email: appointment.customerGuest.email || undefined,
      ...(guestIsMinor && {
        primaryCustomer: {
          firstName: customer.user.firstName || '',
          lastName: customer.user.lastName || '',
          dob: customerDob,
          phoneNumber: customerPhoneNumber,
          email: customer.user.email || '',
          relation: guestRelation,
          label: convertSnakeToTitleCase(
            guestRelation === 'OTHER' ? 'GUARDIAN' : guestRelation,
          ),
        },
      }),
    };
  }

  if (appointment.isForSomeoneElse) {
    const recipientDob = appointment.recipientDob
      ? new Date(appointment.recipientDob).toISOString().split('T')[0]
      : undefined;
    const recipientIsMinor = isMinor(recipientDob, appointment.startDateTime);
    const recipientRelation = appointment.recipientUserRelation ?? 'OTHER';
    return {
      firstName: appointment.recipientFirstName || '',
      lastName: appointment.recipientLastName || '',
      dob: recipientDob,
      phoneNumber: undefined,
      email: undefined,
      ...(recipientIsMinor && {
        primaryCustomer: {
          firstName: customer.user.firstName || '',
          lastName: customer.user.lastName || '',
          dob: customerDob,
          phoneNumber: customerPhoneNumber,
          email: customer.user.email || '',
          relation: recipientRelation,
          label: convertSnakeToTitleCase(
            recipientRelation === 'OTHER' ? 'GUARDIAN' : recipientRelation,
          ),
        },
      }),
    };
  }

  return {
    firstName: customer.user.firstName || '',
    lastName: customer.user.lastName || '',
    dob: customerDob,
    phoneNumber: customerPhoneNumber,
    email: customer.user.email || '',
  };
};
